// Sub-components for the Consistency Intelligence drawer.
// Split out of ConsistencyDrawer.tsx to keep the drawer file focused on layout
// and state; mirrors the Concept layer's parts/helpers separation.
import {
  AlignLeft,
  AlertTriangle,
  Loader2,
  Scale,
  Sparkles,
} from "lucide-react";
import { ScoreRing, StatTile } from "../Domain";
import { LAYER_ICON_BOX_TONE } from "../constants";
import { formatDocType } from "../utils";
import type {
  CDefinitionMismatch,
  ConsistencyDisplayData,
} from "./utilities/types";
import {
  docTypeBadgeClass,
  pluralSuffix,
  severityTone,
} from "./utilities/helpers";
import { CONSISTENCY_COPY } from "./utilities/constants";

export function ConsistencyLoading() {
  return (
    <div className="flex flex-1 items-center justify-center gap-2 text-muted-foreground">
      <Loader2 className="size-5 animate-spin" />
      <span className="text-sm">{CONSISTENCY_COPY.loadingLabel}</span>
    </div>
  );
}

export function ConsistencyEmptyState({ message }: { message: string }) {
  return (
    <div className="p-6 text-center text-sm text-muted-foreground">
      {message}
    </div>
  );
}

export function ContradictionAlert({
  contradictionsDetected,
}: {
  contradictionsDetected: number;
}) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-[12px] text-amber-300">
      <AlertTriangle className="mt-0.5 size-4 flex-none" />
      <div className="min-w-0 break-words">
        <div className="font-semibold text-amber-200">
          {contradictionsDetected} internal contradiction
          {pluralSuffix(contradictionsDetected)} detected
        </div>
        <div className="mt-0.5 opacity-90">
          Review the contradictions below and align the conflicting statements
          before publishing the next revision.
        </div>
      </div>
    </div>
  );
}

export function ConsistencyHeader({
  document,
  documentTitle,
  documentType,
  backendNote,
  overallPct,
}: {
  document: ConsistencyDisplayData["document"] | undefined;
  documentTitle: string | undefined;
  documentType: string | undefined;
  backendNote?: string;
  overallPct: string;
}) {
  const title = documentTitle || document?.title;
  const typeKey = (documentType || document?.document_type || "").toUpperCase();
  const typeBadgeCls = docTypeBadgeClass(typeKey);

  return (
    <header className="sticky top-0 z-10 flex flex-col gap-3 border-b border-border bg-card p-4 pr-14 sm:p-6 sm:pr-16 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <div
          className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${LAYER_ICON_BOX_TONE.consistency.box}`}
        >
          <AlignLeft
            className={`size-5 ${LAYER_ICON_BOX_TONE.consistency.icon}`}
          />
        </div>
        <div className="min-w-0 flex flex-col gap-1.5">
          <div className="text-lg font-bold text-foreground">
            {CONSISTENCY_COPY.title}
          </div>
          <p className="text-xs text-muted-foreground max-w-xl break-words">
            {CONSISTENCY_COPY.description}
          </p>
          {backendNote && (
            <p className="text-[11px] text-muted-foreground max-w-xl break-words">
              {backendNote}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            {title && (
              <span className="inline-flex items-center rounded-md border border-border bg-background/60 px-2 py-0.5 text-[11px] text-foreground break-words">
                {title}
              </span>
            )}
            {typeBadgeCls && (
              <span
                className={`inline-flex items-center justify-center px-2 py-0.5 rounded-[4px] text-[10px] font-bold ${typeBadgeCls}`}
              >
                {formatDocType(typeKey)}
              </span>
            )}
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-400">
              <span className="size-1.5 rounded-full bg-emerald-400" />
              {CONSISTENCY_COPY.validatedLabel}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-start gap-4">
        <div className="flex flex-col items-end">
          <span className="text-[11px] text-muted-foreground">
            {CONSISTENCY_COPY.overallLabel}
          </span>
          <span className="text-3xl font-bold text-emerald-400 tabular-nums">
            {overallPct}
          </span>
        </div>
      </div>
    </header>
  );
}


export function DefinitionMismatchCard({
  item,
}: {
  item: CDefinitionMismatch;
}) {
  const t = severityTone(item.severity);
  const divergence = item.divergence_score ?? 0;
  const definitions = item.definitions ?? [];
  return (
    <div className="rounded-lg border border-border bg-card/60 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Scale className="size-4 text-fuchsia-400" />
          <span className="text-[13px] font-semibold text-foreground break-words">
            {item.concept}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-muted-foreground">
            Divergence · {divergence.toFixed(2)}
          </span>
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${t.bg} ${t.text}`}
          >
            {t.label}
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        {definitions.map((d, i) => (
          <div
            key={i}
            className="rounded border border-border/50 bg-background/60 p-2"
          >
            <div className="text-[12px] text-foreground break-words">
              {d.text}
            </div>
            <div className="mt-1 text-[10.5px] text-muted-foreground">
              {d.location}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PrimaryConsistencyHighlight({
  overallPct,
  contradictions,
  terminology,
  references,
  definitions,
  findings,
}: {
  overallPct: string;
  contradictions: number;
  terminology: number;
  references: number;
  definitions: number;
  findings: number;
}) {
  const affectedTotal = contradictions + terminology + references + definitions;
  return (
    <div className="grid gap-4 p-4 md:grid-cols-[minmax(0,1fr)_260px]">
      <div className="min-w-0 flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <span className="inline-flex items-center rounded-md bg-fuchsia-500/15 px-2 py-0.5 text-[11px] font-bold tracking-wide text-fuchsia-300 border border-fuchsia-500/25">
            SIGNAL
          </span>
          <span className="text-lg font-bold text-foreground break-words">
            {findings} finding{pluralSuffix(findings)} across {affectedTotal}{" "}
            affected statements
          </span>
        </div>

        <div className="rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/10 p-3">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="inline-flex items-center gap-1 rounded-md bg-gradient-to-r from-fuchsia-500 to-violet-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
              <Sparkles className="size-3" />
              AI
            </span>
            <span className="text-[11px] tracking-wide text-fuchsia-300 font-semibold">
              Consistency assessment
            </span>
          </div>
          <p className="text-[12.5px] leading-relaxed text-foreground/90 break-words">
            The document is broadly internally consistent. {contradictions}{" "}
            contradiction{pluralSuffix(contradictions)}, {terminology}{" "}
            terminology variant{pluralSuffix(terminology)} and {references}{" "}
            unresolved reference{pluralSuffix(references)} were found and should
            be resolved in the next revision to lift the consistency score above
            95%.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2.5">
          <StatTile
            value={contradictions}
            label="Contradictions"
            tone="amber"
          />
          <StatTile
            value={terminology}
            label="Terminology variants"
            tone="indigo"
          />
          <StatTile
            value={definitions}
            label="Definition mismatches"
            tone="amber"
          />
          <StatTile
            value={references}
            label="Unresolved references"
            tone="cyan"
          />
        </div>
      </div>

      <div className="flex flex-col items-center gap-3 min-w-0">
        <div className="w-full text-center text-xs text-muted-foreground">
          Consistency index
        </div>
        <ScoreRing
          value={overallPct}
          icon={<AlignLeft className="size-5 text-emerald-400" />}
          caption="Consistent"
        />
      </div>
    </div>
  );
}
